import { EMPTY_VALUE } from "staticConfiguration";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import Box, { type BoxProps } from "@mui/material/Box";
import Divider, { type DividerProps } from "@mui/material/Divider";
import Typography, { type TypographyProps } from "@mui/material/Typography";
import type { TFunction } from "core/entities";
import { useBreakpoints } from "core/hooks/useBreakpoints";
import { useListFormatter } from "core/hooks/useListFormatter";
import { chunkify } from "core/utils/chunkify";
import { Fragment, type ReactNode } from "react";

export function SpecificationsDisplayGrid<
  T extends Record<
    string,
    string | ReactNode | number | undefined | null | string[]
  >,
>({
  fields,
  t,
  boxProps,
  dividerProps,
  columns = 1,
  keyTypographyProps,
  valueTypographyProps,
  forceGrid = false,
  omitFields,
  formatKeys = true,
}: {
  fields: T;
  t: TFunction;
  boxProps?: BoxProps;
  dividerProps?: DividerProps;
  columns?: number;
  keyTypographyProps?: TypographyProps;
  valueTypographyProps?: TypographyProps;
  forceGrid?: boolean;
  omitFields?: Partial<Record<keyof T, boolean>>;
  formatKeys?: boolean;
}) {
  const listFormatter = useListFormatter();
  const visibleFields = Object.entries(fields).filter(
    ([key]) => omitFields?.[key] !== false
  );
  const chunks = chunkify(visibleFields, columns);
  const { isMobile } = useBreakpoints();
  let display = isMobile ? "flex" : "grid";
  if (forceGrid) {
    display = "grid";
  }

  return (
    <Box
      component="dl"
      {...boxProps}
      sx={{
        marginBlockStart: 0,
        marginBlockEnd: 0,
        display,
        flexDirection: "column",
        gridTemplateColumns: `repeat(${columns * 2},1fr)`,
        rowGap: 0.5,
        columnGap: 2,
        ...boxProps?.sx,
      }}
    >
      {chunks.map((entry, index, { length }) => {
        const isLast = index === length - 1;
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: no better key available
          <Fragment key={index}>
            {entry
              .filter(([key]) => omitFields?.[key] !== false)
              .map(([key, value]) => {
                let displayValue = value;
                if (Array.isArray(value)) {
                  if (value.length === 0) {
                    displayValue = EMPTY_VALUE;
                  } else {
                    displayValue = listFormatter.format(value);
                  }
                }
                if (typeof value === "boolean") {
                  displayValue = value ? (
                    <CheckIcon fontSize="small" color="inherit" />
                  ) : (
                    <CloseIcon fontSize="small" color="inherit" />
                  );
                }
                if (value === null || value === undefined) {
                  displayValue = EMPTY_VALUE;
                }
                return (
                  <Fragment key={key}>
                    <Typography
                      component="dt"
                      data-testid={`specification-key_${key}`}
                      variant="body2"
                      color="primary.dark"
                      sx={{ fontWeight: "bold" }}
                      {...keyTypographyProps}
                    >
                      {formatKeys ? t(key) : key}
                    </Typography>
                    <Typography
                      data-testid={`specification-value_${key}`}
                      component="dd"
                      variant="body2"
                      {...valueTypographyProps}
                    >
                      {displayValue || EMPTY_VALUE}
                    </Typography>
                  </Fragment>
                );
              })}
            {isLast ? null : (
              <Divider sx={{ gridColumn: "1 / -1" }} {...dividerProps} />
            )}
          </Fragment>
        );
      })}
    </Box>
  );
}
